import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { hashEmail, pushToDataLayer } from '../utils/gtm';
import { GTM_CONSTS, GTM_EVENTS } from '../constants/gtmEvents';
import { useTranslation } from 'react-i18next';
import { useAuth0 } from '@auth0/auth0-react';
import { stripLanguageFromPath } from '../utils/languageUrl';

const getTemplateFromPath = pathname => {
  switch (pathname) {
    case '/':
      return 'Home page';
    case '/unauthorized':
      return 'Error page';
    case '/status':
      return 'Status page';
    default:
      return 'Error page';
  }
};

export const useGTMPageView = (pageData = {}) => {
  const { user, isAuthenticated, isLoading } = useAuth0();
  const location = useLocation();
  const { i18n } = useTranslation();
  const lastTrackedPath = useRef(null);
  const normalizedPathname = stripLanguageFromPath(location.pathname);
  useEffect(() => {
    if (isLoading) return;

    const currentState = `${
      normalizedPathname
    }-${isAuthenticated}-${user?.email || 'no-email'}`;
    if (lastTrackedPath.current === currentState) return;

    const sendPageView = async () => {
      let userHashedEmail = '';

      if (isAuthenticated && user?.email) {
        userHashedEmail = await hashEmail(user.email);
      }

      const defaultPageData = {
        event: GTM_EVENTS.PAGE_VIEW,
        business_unit: GTM_CONSTS.EUROPE_OPERATIONS,
        from_web_view: /wv|webview|iPhone.*Safari\//.test(navigator.userAgent)
          ? 'true'
          : 'false',
        page_language: i18n.language || 'en',
        page_title: document.title,
        page_top_category: GTM_CONSTS.PAGE_CATEGORY_HOME,
        page_scope: 'global',
        template: getTemplateFromPath(normalizedPathname),
        page_path: normalizedPathname,
        page_type: 'page',
        digital_platform: 'Samples Feller',
        digital_platform_country: 'CH',
        environment:
          process.env.REACT_APP_ENVIRONMENT === 'production'
            ? 'production'
            : 'development',
        ...pageData,
      };

      if (userHashedEmail) {
        defaultPageData.user_hashed_email = userHashedEmail;
      }

      pushToDataLayer(defaultPageData);

      lastTrackedPath.current = currentState;
    };

    sendPageView();
  }, [
    normalizedPathname,
    isAuthenticated,
    isLoading,
    user?.email,
    i18n.language,
    pageData,
  ]);
};
